import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Sparkles, ArrowRight, RotateCcw } from 'lucide-react';
import { LeadInfo } from '../types';
interface GateProps {
  leadInfo: LeadInfo;
  setLeadInfo: React.Dispatch<React.SetStateAction<LeadInfo>>;
  onSubmit: () => void;
  onRestart: () => void;
}
export function Gate({
  leadInfo,
  setLeadInfo,
  onSubmit,
  onRestart
}: GateProps) {
  const [errors, setErrors] = useState<Partial<Record<keyof LeadInfo, string>>>({});
  const validate = () => {
    const newErrors: Partial<Record<keyof LeadInfo, string>> = {};
    if (!leadInfo.fullName) newErrors.fullName = 'Required';
    if (!leadInfo.companyName) newErrors.companyName = 'Required';
    if (!leadInfo.title) newErrors.title = 'Required';
    if (!leadInfo.workEmail) {
      newErrors.workEmail = 'Required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadInfo.workEmail)) {
      newErrors.workEmail = 'Invalid email';
    }
    if (!leadInfo.linkedInUrl) newErrors.linkedInUrl = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit();
    }
  };
  const handleChange = (field: keyof LeadInfo) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setLeadInfo({
      ...leadInfo,
      [field]: e.target.value
    });
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined
      });
    }
  };
  return <div className="min-h-screen bg-mint/30 relative flex items-center justify-center p-6" data-id="element-19">
      {/* Fake blurred background to look like results */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40 blur-md" data-id="element-20">
        <div className="max-w-5xl mx-auto pt-20 px-6" data-id="element-21">
          <div className="w-full h-32 bg-white rounded-3xl mb-8" data-id="element-22" />
          <div className="w-full h-64 bg-white rounded-3xl mb-8" data-id="element-23" />
          <div className="grid grid-cols-2 gap-8" data-id="element-24">
            <div className="h-40 bg-white rounded-3xl" data-id="element-25" />
            <div className="h-40 bg-white rounded-3xl" data-id="element-26" />
          </div>
        </div>
      </div>

      <div className="absolute inset-0 bg-lavender/60 backdrop-blur-xl z-0" data-id="element-27" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      type: 'spring',
      stiffness: 100,
      damping: 20
    }} className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-soft-lg max-w-xl w-full relative z-10 border-4 border-white" data-id="element-28">
        <div className="w-16 h-16 bg-grape text-white rounded-full flex items-center justify-center mb-6 shadow-soft-sm mx-auto relative" data-id="element-29">
          <Lock size={28} data-id="element-30" />
          <Sparkles size={20} className="absolute -top-2 -right-2 text-sunshine" data-id="element-31" />
        </div>

        <div className="text-center mb-8" data-id="element-32">
          <h2 className="text-3xl font-extrabold text-ink mb-3" data-id="element-33">
            Almost there!
          </h2>
          <p className="text-ink/70 font-medium text-lg" data-id="element-34">
            Your custom priority plan is ready. Where should we send your
            results?
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-35">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-36">
            <div data-id="element-37">
              <input type="text" placeholder="Full Name" value={leadInfo.fullName} onChange={handleChange('fullName')} className={`w-full px-5 py-4 rounded-2xl bg-ink/5 border-2 outline-none font-medium transition-colors ${errors.fullName ? 'border-coral focus:border-coral' : 'border-transparent focus:border-grape focus:bg-white'}`} data-id="element-38" />
            </div>
            <div data-id="element-39">
              <input type="text" placeholder="Company Name" value={leadInfo.companyName} onChange={handleChange('companyName')} className={`w-full px-5 py-4 rounded-2xl bg-ink/5 border-2 outline-none font-medium transition-colors ${errors.companyName ? 'border-coral focus:border-coral' : 'border-transparent focus:border-grape focus:bg-white'}`} data-id="element-40" />
            </div>
          </div>

          <div data-id="element-41">
            <input type="text" placeholder="Job Title" value={leadInfo.title} onChange={handleChange('title')} className={`w-full px-5 py-4 rounded-2xl bg-ink/5 border-2 outline-none font-medium transition-colors ${errors.title ? 'border-coral focus:border-coral' : 'border-transparent focus:border-grape focus:bg-white'}`} data-id="element-42" />
          </div>

          <div data-id="element-43">
            <input type="email" placeholder="Work Email" value={leadInfo.workEmail} onChange={handleChange('workEmail')} className={`w-full px-5 py-4 rounded-2xl bg-ink/5 border-2 outline-none font-medium transition-colors ${errors.workEmail ? 'border-coral focus:border-coral' : 'border-transparent focus:border-grape focus:bg-white'}`} data-id="element-44" />
            {errors.workEmail && <p className="text-coral text-sm mt-1 ml-2 font-semibold" data-id="element-45">
                {errors.workEmail}
              </p>}
          </div>

          <div data-id="element-46">
            <input type="url" placeholder="LinkedIn URL" value={leadInfo.linkedInUrl} onChange={handleChange('linkedInUrl')} className={`w-full px-5 py-4 rounded-2xl bg-ink/5 border-2 outline-none font-medium transition-colors ${errors.linkedInUrl ? 'border-coral focus:border-coral' : 'border-transparent focus:border-grape focus:bg-white'}`} data-id="element-47" />
          </div>

          <motion.button whileHover={{
          scale: 1.02,
          y: -2
        }} whileTap={{
          scale: 0.98
        }} type="submit" className="w-full bg-ink text-white font-bold text-xl py-5 rounded-full shadow-soft hover:shadow-soft-sm transition-all mt-6 flex items-center justify-center gap-2" data-id="element-48">
            Show my results
            <ArrowRight size={24} data-id="element-49" />
          </motion.button>

          <p className="text-center text-xs font-semibold text-ink/40 mt-4" data-id="element-50">
            We respect your privacy. No spam, ever.
          </p>

          <button type="button" onClick={onRestart} className="w-full mt-2 inline-flex items-center justify-center gap-2 py-2 rounded-full text-ink/50 font-bold text-sm hover:text-ink transition-colors" data-id="element-51">
            <RotateCcw size={14} strokeWidth={3} data-id="element-52" />
            Start over
          </button>
        </form>
      </motion.div>
    </div>;
}